import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Youtube, Instagram, Facebook, Plug, Send, Calendar, Clock, CheckCircle2, Info, Loader2 } from "lucide-react";

const PLATFORMS = [
  { key: "youtube", label: "YouTube", icon: Youtube, color: "#FF0000", note: "Data API v3" },
  { key: "instagram", label: "Instagram", icon: Instagram, color: "#E1306C", note: "Business/Creator account" },
  { key: "facebook", label: "Facebook", icon: Facebook, color: "#1877F2", note: "Page publishing" },
];

export default function Publish() {
  const [params] = useSearchParams();
  const [connections, setConnections] = useState([]);
  const [projects, setProjects] = useState([]);
  const [logs, setLogs] = useState([]);
  const [selected, setSelected] = useState([]);
  const [projectId, setProjectId] = useState(params.get("project") || "");
  const [title, setTitle] = useState("");
  const [caption, setCaption] = useState("");
  const [hashtags, setHashtags] = useState("");
  const [schedule, setSchedule] = useState("");
  const [publishing, setPublishing] = useState(false);

  const load = () => {
    api.get("/connections").then((r) => setConnections(r.data));
    api.get("/projects").then((r) => setProjects(r.data.filter((p) => p.status === "ready")));
    api.get("/publish/logs").then((r) => setLogs(r.data));
  };
  useEffect(load, []);

  const isConnected = (p) => connections.find((c) => c.platform === p && c.connected);

  const connect = async (platform) => {
    const name = window.prompt(`Enter your ${platform} account/handle name to connect:`);
    if (!name) return;
    try {
      await api.post("/connections", { platform, account_name: name });
      toast.success(`${platform} connected`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const disconnect = async (platform) => {
    await api.delete(`/connections/${platform}`);
    toast.success("Disconnected");
    load();
  };

  const toggle = (p) => setSelected((s) => (s.includes(p) ? s.filter((x) => x !== p) : [...s, p]));

  const submit = async () => {
    if (!projectId) return toast.error("Select a rendered project");
    if (selected.length === 0) return toast.error("Select at least one platform");
    setPublishing(true);
    try {
      const { data } = await api.post("/publish", {
        project_id: projectId, platforms: selected, title, caption, hashtags,
        schedule_at: schedule || null,
      });
      toast.success(data.status === "scheduled" ? "Scheduled!" : "Published!");
      setSelected([]); setTitle(""); setCaption(""); setHashtags(""); setSchedule("");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setPublishing(false);
    }
  };

  return (
    <div className="min-h-screen p-10">
      <div className="rf-fade-up">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#00E5FF]">Distribution</p>
        <h1 className="mt-1 font-heading text-4xl font-bold">Publishing</h1>
        <p className="mt-2 text-gray-400">Connect platforms and push your branded reels everywhere.</p>
      </div>

      <div className="mt-6 flex items-start gap-2 rounded-md border border-[#00E5FF]/20 bg-[#00E5FF]/5 p-4 text-sm text-gray-300">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-[#00E5FF]" />
        <span>Publishing here is <b>simulated</b> and logged for your workflow. Real posting to YouTube/Instagram/Facebook requires each platform's OAuth API credentials (and a Business account for IG/FB) — plug those in to go live.</span>
      </div>

      <div className="mt-8 grid grid-cols-3 gap-5">
        {PLATFORMS.map((p) => {
          const conn = isConnected(p.key);
          return (
            <div key={p.key} data-testid={`platform-${p.key}`} className="rounded-lg border border-white/10 bg-[#121212] p-5">
              <div className="flex items-center justify-between">
                <p.icon className="h-6 w-6" style={{ color: p.color }} />
                {conn && <CheckCircle2 className="h-5 w-5 text-[#34C759]" />}
              </div>
              <h3 className="mt-4 font-heading text-lg font-semibold">{p.label}</h3>
              <p className="text-xs text-gray-500">{conn ? conn.account_name : p.note}</p>
              {conn ? (
                <button onClick={() => disconnect(p.key)} data-testid={`disconnect-${p.key}`} className="mt-4 w-full rounded-md border border-white/10 py-2 text-sm text-gray-400 hover:bg-white/5">Disconnect</button>
              ) : (
                <button onClick={() => connect(p.key)} data-testid={`connect-${p.key}`} className="mt-4 flex w-full items-center justify-center gap-1.5 rounded-md bg-[#00E5FF] py-2 text-sm font-semibold text-black hover:bg-[#33EBFF]">
                  <Plug className="h-4 w-4" /> Connect
                </button>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="rounded-lg border border-white/10 bg-[#121212] p-6">
          <h3 className="mb-4 font-heading text-lg font-semibold">Compose Post</h3>
          <label className="mb-1.5 block text-sm text-gray-400">Project (rendered)</label>
          <select value={projectId} onChange={(e) => setProjectId(e.target.value)} data-testid="publish-project-select"
            className="mb-4 h-10 w-full rounded-md border border-white/10 bg-[#1A1A1A] px-2 text-sm text-white focus:border-[#00E5FF] focus:outline-none">
            <option value="">Select a project…</option>
            {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>

          <label className="mb-1.5 block text-sm text-gray-400">Platforms</label>
          <div className="mb-4 flex gap-2">
            {PLATFORMS.map((p) => {
              const enabled = isConnected(p.key);
              return (
                <button key={p.key} disabled={!enabled} onClick={() => toggle(p.key)} data-testid={`select-platform-${p.key}`}
                  className={`flex flex-1 items-center justify-center gap-1.5 rounded-md border py-2 text-sm ${!enabled ? "cursor-not-allowed border-white/5 text-gray-700" : selected.includes(p.key) ? "border-[#00E5FF] bg-[#00E5FF]/10 text-[#00E5FF]" : "border-white/10 text-gray-400 hover:bg-white/5"}`}>
                  <p.icon className="h-4 w-4" /> {p.label}
                </button>
              );
            })}
          </div>

          <Input label="Title" value={title} onChange={setTitle} testId="publish-title" />
          <div className="mb-4">
            <label className="mb-1.5 block text-sm text-gray-400">Caption</label>
            <textarea value={caption} onChange={(e) => setCaption(e.target.value)} rows={3} data-testid="publish-caption"
              className="w-full rounded-md border border-white/10 bg-[#1A1A1A] p-2 text-sm text-white focus:border-[#00E5FF] focus:outline-none" />
          </div>
          <Input label="Hashtags" value={hashtags} onChange={setHashtags} placeholder="#reels #brand" testId="publish-hashtags" />
          <div className="mb-4">
            <label className="mb-1.5 flex items-center gap-1.5 text-sm text-gray-400"><Calendar className="h-3.5 w-3.5" /> Schedule (optional)</label>
            <input type="datetime-local" value={schedule} onChange={(e) => setSchedule(e.target.value)} data-testid="publish-schedule"
              className="h-10 w-full rounded-md border border-white/10 bg-[#1A1A1A] px-2 text-sm text-white focus:border-[#00E5FF] focus:outline-none" />
          </div>

          <button onClick={submit} disabled={publishing} data-testid="publish-submit-btn"
            className="flex h-11 w-full items-center justify-center gap-2 rounded-md bg-[#FF007F] font-semibold text-white transition-colors hover:bg-[#ff3399] disabled:opacity-60">
            {publishing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            {schedule ? "Schedule Post" : "Publish Now"}
          </button>
        </div>

        <div className="rounded-lg border border-white/10 bg-[#121212] p-6">
          <h3 className="mb-4 font-heading text-lg font-semibold">Activity Log</h3>
          {logs.length === 0 ? (
            <p className="py-8 text-center text-sm text-gray-500">No publishes yet.</p>
          ) : (
            <div className="space-y-3">
              {logs.map((l) => (
                <div key={l.id} data-testid={`log-${l.id}`} className="rounded-md border border-white/5 bg-[#0f0f0f] p-3">
                  <div className="flex items-center justify-between">
                    <span className="truncate font-medium">{l.project_name}</span>
                    <span className={`flex items-center gap-1 text-xs ${l.status === "scheduled" ? "text-[#00E5FF]" : "text-[#34C759]"}`}>
                      {l.status === "scheduled" ? <Clock className="h-3 w-3" /> : <CheckCircle2 className="h-3 w-3" />}
                      {l.status}
                    </span>
                  </div>
                  <div className="mt-1.5 flex flex-wrap gap-1">
                    {l.platforms.map((p) => <span key={p} className="rounded bg-white/5 px-1.5 py-0.5 text-[10px] uppercase text-gray-400">{p}</span>)}
                  </div>
                  {l.title && <p className="mt-1.5 truncate text-xs text-gray-500">{l.title}</p>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Input({ label, value, onChange, placeholder, testId }) {
  return (
    <div className="mb-4">
      <label className="mb-1.5 block text-sm text-gray-400">{label}</label>
      <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testId}
        className="h-10 w-full rounded-md border border-white/10 bg-[#1A1A1A] px-2 text-sm text-white focus:border-[#00E5FF] focus:outline-none" />
    </div>
  );
}
